import { Component, EventEmitter, Input, OnDestroy, OnInit, AfterViewInit, Output, ViewChild } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { AvailableServices, MeetingViewModelService } from '@vendasta/integrations';
import { MeetingSource, Preferences } from '@vendasta/meetings';
import { BehaviorSubject, Observable, ReplaySubject, Subscription, combineLatest, from, of } from 'rxjs';
import {
  catchError,
  distinctUntilChanged,
  filter,
  map,
  mapTo,
  shareReplay,
  skip,
  switchMap,
  take,
  tap,
} from 'rxjs/operators';
import { MeetingSchedulerStoreService } from '../../data-providers/meeting-scheduler-store.service';
import { IntegrationSelectionComponent, IntegrationServiceSelection } from '../../meeting-integrations/integration-selection/integration-selection.component';
import { ScheduleSettingsViewService } from '../schedule-settings-view.service';
import { isInSettingsMode } from '../../utils';
import { ProductAnalyticsService } from '@vendasta/product-analytics';
import { POSTHOG_ACTIONS, POSTHOG_CATEGORIES, POSTHOG_KEYS, getMeetingAppDisplayName } from '../../constants';

const meetingSourceToAvailableService = new Map<MeetingSource, AvailableServices>();
meetingSourceToAvailableService.set(MeetingSource.MEETING_SOURCE_GOOGLE_MEET, AvailableServices.GOOGLE_MEET);
meetingSourceToAvailableService.set(MeetingSource.MEETING_SOURCE_ZOOM, AvailableServices.ZOOM);
meetingSourceToAvailableService.set(MeetingSource.MEETING_SOURCE_MICROSOFT, AvailableServices.MICROSOFT);

const availableServiceToMeetingSource = new Map<AvailableServices, MeetingSource>();
availableServiceToMeetingSource.set(AvailableServices.GOOGLE_MEET, MeetingSource.MEETING_SOURCE_GOOGLE_MEET);
availableServiceToMeetingSource.set(AvailableServices.ZOOM, MeetingSource.MEETING_SOURCE_ZOOM);
availableServiceToMeetingSource.set(AvailableServices.MICROSOFT, MeetingSource.MEETING_SOURCE_MICROSOFT);

@Component({
  selector: 'meeting-scheduler-settings-meeting-app',
  templateUrl: './meeting-app-card.component.html',
  styleUrls: ['./meeting-app-card.component.scss'],
  standalone: false,
})
export class MeetingAppCardComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() serviceKey: string;
  @Input() isGoogleMeetConfigured: boolean;
  @Input() isMicrosoftConfigured: boolean;
  @Input() enableTracking: boolean;

  @Input() set hostPreferences(preferences: Preferences) {
    this.hostPreferences$$.next(preferences);
  }

  @Output() integrationSelection: EventEmitter<IntegrationServiceSelection> = new EventEmitter();

  initialServiceSelection$: Observable<AvailableServices>;
  selection$: Observable<IntegrationServiceSelection>;

  readonly meet = AvailableServices.GOOGLE_MEET;
  readonly zoom = AvailableServices.ZOOM;
  readonly microsoft = AvailableServices.MICROSOFT;

  @ViewChild(IntegrationSelectionComponent) integrationSelectionComponent: IntegrationSelectionComponent;

  private readonly selection$$: BehaviorSubject<IntegrationServiceSelection> = new BehaviorSubject(undefined);
  private readonly subscriptions: Subscription[] = [];
  private readonly hostPreferences$$: ReplaySubject<Preferences> = new ReplaySubject(1);
  private previousSelection: IntegrationServiceSelection | null = null;

  constructor(
    private readonly meetingSchedulerStoreService: MeetingSchedulerStoreService,
    private readonly scheduleSettingsViewService: ScheduleSettingsViewService,
    private readonly alertService: SnackbarService,
    private readonly analyticsService: ProductAnalyticsService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly meetingViewModelService: MeetingViewModelService,
  ) {}

  ngOnInit(): void {
    // Refresh connection status when navigating back to this page
    // This ensures we have the latest connection state after returning from integration settings
    this.subscriptions.push(
      this.router.events
        .pipe(
          filter((event) => event instanceof NavigationEnd),
          distinctUntilChanged((prev: NavigationEnd, curr: NavigationEnd) => prev.url === curr.url),
          filter(() => {
            // Only refresh if we're on the settings page (not navigating away)
            const url = this.router.url;
            return url.includes('settings') || url.includes('schedule');
          }),
        )
        .subscribe(() => {
          // Refresh connection status by triggering re-check
          // Use setTimeout to ensure ViewChild is available
          setTimeout(() => this.refreshConnectionStatus(), 100);
        }),
    );

    this.selection$ = this.selection$$.pipe(filter<IntegrationServiceSelection>(Boolean));

    const hostServicePreference$ = this.hostPreferences$$.pipe(
      filter<Preferences>(Boolean),
      map((preferences) => meetingSourceToAvailableService.get(preferences.meetingIntegration)),
    );
    this.initialServiceSelection$ = combineLatest([
      this.scheduleSettingsViewService.meetingAppSelection$,
      hostServicePreference$,
    ]).pipe(
      map(([selection, preference]) => {
        return selection || preference;
      }),
      filter<AvailableServices>(Boolean),
    );

    const setPreference$ = this.selection$.pipe(
      tap((s) => this.scheduleSettingsViewService.setMeetingAppSelection(s.service)),
      filter((s) => s.isConnected),
      distinctUntilChanged((s1, s2) => s1.service === s2.service && s1.isConnected === s2.isConnected),
      switchMap((selection) =>
        from(
          this.meetingSchedulerStoreService.updateUserPreferences({
            meetingIntegration: availableServiceToMeetingSource.get(selection.service),
          }),
        ),
      ),
      shareReplay(1),
    );
    this.subscriptions.push(
      setPreference$.subscribe({
        error: () =>
          this.alertService.openErrorSnack(
            'MEETING_SCHEDULER.SCHEDULE_SETTINGS.ALERTS.SET_PREFERRED_MEETING_APP_ERROR',
          ),
      }),
    );
    // Skip the first call because we call updatePreference initially because we want there to be a preference set.
    this.subscriptions.push(
      setPreference$
        .pipe(
          skip(1),
          mapTo(true),
          catchError(() => of(false)),
        )
        .subscribe((success) => {
          if (success) {
            // Original tracking for all contexts
            if (this.enableTracking) {
              this.analyticsService.trackEvent(
                POSTHOG_KEYS.HOST_PREFERENCE_UPDATED,
                POSTHOG_CATEGORIES.USER,
                POSTHOG_ACTIONS.CLICK,
              );
            }

            // Settings-specific tracking for meeting app changes
            // Only track MEETING_APP_CHANGED events when not in setup wizard mode
            // This ensures we differentiate between initial setup and ongoing changes
            if (isInSettingsMode(this.route)) {
              const currentSelection = this.selection$$.value;
              if (currentSelection) {
                this.trackMeetingAppChange(currentSelection);
              }
            }

            this.alertService.openSuccessSnack(
              'MEETING_SCHEDULER.SCHEDULE_SETTINGS.ALERTS.SET_PREFERRED_MEETING_APP_SUCCESS',
            );
          }
        }),
    );

    // Track OAuth completion for meeting apps
    // This subscription detects when a previously unconnected app becomes connected
    // after an OAuth flow, which is a common scenario in the settings page
    this.subscriptions.push(
      this.selection$.subscribe((selection) => {
        if (isInSettingsMode(this.route) && this.previousSelection) {
          // Check if same app became connected (OAuth completion)
          const wasNotConnected =
            this.previousSelection.service === selection.service && !this.previousSelection.isConnected;
          const isNowConnected = selection.isConnected;

          if (wasNotConnected && isNowConnected) {
            // App just became connected via OAuth - track the change
            this.trackMeetingAppChange(selection);
          }
        }
        // Always update previous selection for next comparison
        this.previousSelection = selection;
      }),
    );
  }

  ngAfterViewInit(): void {
    // Refresh on initial load after view is initialized to ensure we have the latest state
    setTimeout(() => this.refreshConnectionStatus(), 100);
  }

  private trackMeetingAppChange(selection: IntegrationServiceSelection): void {
    // Only track meeting app changes in settings mode for connected apps
    // This prevents false tracking during initialization or for unconnected apps
    // Connected apps are the only ones where user can actually use the selection
    if (this.enableTracking && selection.isConnected) {
      this.analyticsService.trackEvent(
        POSTHOG_KEYS.MEETING_APP_CHANGED,
        POSTHOG_CATEGORIES.USER,
        POSTHOG_ACTIONS.CLICK,
        undefined,
        {
          calendar_or_meeting_provider: getMeetingAppDisplayName(selection.service),
        },
      );
    }
  }

  onIntegrationSelected(selection: IntegrationServiceSelection): void {
    this.selection$$.next(selection);
    this.integrationSelection.next(selection);

    // Track immediate selection of already-connected apps in settings
    // If user switches to a different app that's already connected, track immediately
    // This handles the case where no OAuth flow is needed
    if (isInSettingsMode(this.route) && selection.isConnected) {
      const isDifferentApp = !this.previousSelection || selection.service !== this.previousSelection.service;

      if (isDifferentApp) {
        // User selected a different, already-connected app - track immediately
        this.trackMeetingAppChange(selection);
      }
    }
  }

  private refreshConnectionStatus(): void {
    // Trigger the integration selection component to re-check connection status
    // by re-setting the form control value, which will cause it to re-subscribe
    // to the connection status observables. Since the observables use shareReplay(1),
    // we need to force a re-evaluation by changing and then restoring the value.
    setTimeout(() => {
      if (this.integrationSelectionComponent) {
        const currentSelection = this.selection$$.value;
        if (currentSelection?.service) {
          // Get the current form control value
          const formControl = this.integrationSelectionComponent['integrationFormControl'] as any;
          if (formControl) {
            const currentService = currentSelection.service;
            // Temporarily set to null (without emitting), then back to original value
            // This forces the valueChanges to fire and re-check connection status
            formControl.setValue(null, { emitEvent: false });
            setTimeout(() => {
              formControl.setValue(currentService);
            }, 100);
          }
        } else {
          // If no current selection, check the initial selection from the observable
          this.initialServiceSelection$.pipe(take(1)).subscribe((service) => {
            if (service && this.integrationSelectionComponent) {
              this.integrationSelectionComponent.setSelected(service);
            }
          });
        }
      }
    }, 200);
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }
}
